import React, { createContext, useReducer, useEffect } from 'react';
import { notification } from 'antd';
import { authService } from '../services';
import { jwtDecode } from '../utils/jwtHelpers';
import { AUTH_STORAGE_KEY, LAST_TOKEN_SET_KEY } from '../constants';
import { AuthState, User, AuthAction, AuthContextType, LoginCredentials, PermissionLevel } from '../types';
import { permissionService } from '../../../services/permissionService';
import { roleService } from '../../../services/roleService';

import { createLogger } from '../../../utils/logger';
const logger = createLogger('features-auth');

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  loading: true,
  error: null,
  permissions: []
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_INIT':
      return { ...state, loading: true };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        loading: false,
        error: null
      };
    case 'PERMISSIONS_LOADED':
      return {
        ...state,
        permissions: action.payload
      };
    case 'AUTH_ERROR':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
        error: action.payload,
        permissions: []
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
        error: null,
        permissions: []
      };
    default:
      return state;
  }
}

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  /**
   * Load permissions for a user based on their role (OPTIMIZED)
   * Uses the new endpoint that returns permissions with resource names included
   */
  const loadPermissions = async (user: User): Promise<void> => {
    try {
      let roleId: string | null = null;

      // Check if roleId is already in the user object (from JWT)
      if (user.roleId) {
        roleId = user.roleId;
      } else {
        // Get roleId dynamically from backend
        roleId = await roleService.getRoleIdByName(user.role);
      }

      if (!roleId) {
        logger.error('Unknown role - Could not fetch roleId');
        return;
      }

      // Use optimized endpoint that returns permissions WITH resource names
      const permissions = await permissionService.getRolePermissionsWithNames(roleId);

      dispatch({
        type: 'PERMISSIONS_LOADED',
        payload: permissions
      });
    } catch (error) {
      logger.error('Failed to load permissions');
      // Don't fail the login if permissions fail to load
      // User will just have no permissions cached
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      dispatch({ type: 'AUTH_INIT' });

      // No need to load resources separately anymore - they come with permissions!

      const token = localStorage.getItem(AUTH_STORAGE_KEY);

      if (token) {
        try {
          // const isValid = await authService.validateToken(token);
          // TODO: Use authService.validateToken(token) when endpoints are available in dev stage
          const isValid = true;

          if (isValid) {
            const userData = jwtDecode(token);

            // Fetch additional user details for session restore
            try {
              const userDetails = await authService.getUserDetails(userData.email, token);

              if (userDetails && userDetails.allowedAccountIds) {
                userData.allowedAccountIds = userDetails.allowedAccountIds;
              }
            } catch (detailsError) {
              logger.warn('Failed to fetch user details during session restore');
              // Continue without allowedAccountIds - don't fail session restore
            }

            dispatch({
              type: 'LOGIN_SUCCESS',
              payload: {
                user: userData,
                token
              }
            });

            // Load permissions after successful auth restore
            await loadPermissions(userData);

            // Clear all persistent notifications when session is restored
            notification.destroy();
          } else {
            localStorage.removeItem(AUTH_STORAGE_KEY);
            dispatch({ type: 'AUTH_ERROR', payload: 'Sesión expirada' });
          }
        } catch (error) {
          localStorage.removeItem(AUTH_STORAGE_KEY);
          dispatch({ type: 'AUTH_ERROR', payload: error.message });
        }
      } else {
        dispatch({ type: 'AUTH_ERROR', payload: null });
      }
    };

    initAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      dispatch({ type: 'AUTH_INIT' });
      const response = await authService.login(credentials);
      const { accessToken, refreshToken, user } = response;

      localStorage.setItem(AUTH_STORAGE_KEY, accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      sessionStorage.setItem(LAST_TOKEN_SET_KEY, String(Date.now()));

      // Fetch additional user details (allowedAccountIds)
      try {
        const userDetails = await authService.getUserDetails(credentials.email, accessToken);

        if (userDetails && userDetails.allowedAccountIds) {
          user.allowedAccountIds = userDetails.allowedAccountIds;
        }
      } catch (detailsError) {
        logger.warn('Failed to fetch user details');
        // Continue without allowedAccountIds - don't fail login
      }

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, token: accessToken }
      });

      // Load permissions after successful login
      await loadPermissions(user);

      // Clear all persistent notifications on successful login
      notification.destroy();

      return true;
    } catch (error) {
      dispatch({ type: 'AUTH_ERROR', payload: error.message });
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    localStorage.removeItem('refreshToken');
    dispatch({ type: 'LOGOUT' });
  };

  /**
   * Check if user has permission to access a resource (OPTIMIZED)
   *
   * @param resourceIdentifier - The UUID or name of the resource to check
   * @param requiredLevel - Optional minimum permission level required (defaults to VIEW)
   * @returns True if user has sufficient permission
   */
  const hasPermission = (
    resourceIdentifier: string,
    requiredLevel: PermissionLevel = PermissionLevel.VIEW
  ): boolean => {
    if (!state.permissions || state.permissions.length === 0) {
      return false;
    }

    // Determine if resourceIdentifier is a UUID or a name
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(resourceIdentifier);

    let permission;
    if (isUUID) {
      // Direct UUID lookup
      permission = state.permissions.find((p) => p.resourceId === resourceIdentifier);
    } else {
      // Name-based lookup: Use resourceName field directly (no cache needed!)
      permission = state.permissions.find((p) => p.resourceName?.toLowerCase() === resourceIdentifier.toLowerCase());

      if (!permission) {
        logger.warn('Resource not found in permissions');
        return false;
      }
    }

    if (!permission) {
      return false; // No permission record = no access
    }

    if (permission.status !== 'active') {
      return false; // Inactive permission = no access
    }

    // If we only need VIEW access, both VIEW and EDIT satisfy this
    if (requiredLevel === PermissionLevel.VIEW) {
      return permission.permission === PermissionLevel.VIEW || permission.permission === PermissionLevel.EDIT;
    }

    // If we need EDIT access, only EDIT satisfies this
    if (requiredLevel === PermissionLevel.EDIT) {
      return permission.permission === PermissionLevel.EDIT;
    }

    return false;
  };

  const value = {
    ...state,
    login,
    logout,
    hasPermission
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 